import { useCallback, useRef, useState } from 'react'
import type {
  LearningAnswerOption,
  LearningContentItem,
  LearningLessonDetail,
  LearningQuestionItem,
} from '@services/firebase/learning'
import {
  getLearningLocaleFallbacks,
  normalizeLearningLocale,
  resolveLocalizedMapText,
  resolveLocalizedDisplayText,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'

export function getVisibleContent(content: readonly LearningContentItem[] | undefined) {
  return (content ?? []).filter((item) => Boolean(item.status))
}

export function getVisibleQuestions(questions: readonly LearningQuestionItem[] | undefined) {
  return (questions ?? []).filter((item) => item.status !== false)
}

export function getVisibleAnswers(
  answers:
    | readonly (LearningAnswerOption | string)[]
    | LearningAnswerOption
    | string
    | undefined,
) {
  const normalizedAnswers = Array.isArray(answers)
    ? answers
    : answers
      ? [answers]
      : []

  return normalizedAnswers.filter((answer) => {
    if (typeof answer === 'string') {
      return true
    }

    return answer.status !== false
  })
}

export function getPlayableAudio(value: LearningContentItem | LearningLessonDetail) {
  // Reader boundaries project the learner's preferred recording into audio.
  return value.audio
}

export type LegacyAudioPlaybackState = 'failed' | 'idle' | 'playing' | 'starting'

export type LegacyAudioPlaybackFailure =
  | 'missing-audio'
  | 'not-allowed'
  | 'not-supported'
  | 'unavailable'

export type LegacyAudioPlaybackResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: LegacyAudioPlaybackFailure }

const legacyAudioStatusMessages = {
  failed: {
    de: 'Audiowiedergabe ist nicht verfügbar. Tippen Sie, um es erneut zu versuchen.',
    en: 'Audio playback is unavailable. Tap to try again.',
    es: 'No se puede reproducir el audio. Pulsa para intentarlo de nuevo.',
    fr: 'La lecture audio est indisponible. Touchez pour réessayer.',
    ja: '音声を再生できません。タップしてもう一度お試しください。',
    ko: '오디오를 재생할 수 없습니다. 탭하여 다시 시도하세요.',
    ru: 'Воспроизведение аудио недоступно. Нажмите, чтобы попробовать снова.',
    vi: 'Không thể phát âm thanh. Chạm để thử lại.',
    zh: '音频播放不可用。点按以重试。',
  },
  starting: {
    de: 'Audio wird geladen.',
    en: 'Loading audio.',
    es: 'Cargando el audio.',
    fr: 'Chargement de l’audio.',
    ja: '音声を読み込み中です。',
    ko: '오디오를 불러오는 중입니다.',
    ru: 'Загрузка аудио.',
    vi: 'Đang tải âm thanh.',
    zh: '正在加载音频。',
  },
} as const

function getPlaybackFailureReason(error: unknown): LegacyAudioPlaybackFailure {
  if (error && typeof error === 'object' && 'name' in error) {
    const name = String(error.name)

    if (name === 'NotAllowedError') {
      return 'not-allowed'
    }

    if (name === 'NotSupportedError') {
      return 'not-supported'
    }
  }

  return 'unavailable'
}

export async function playAudio(
  audio: HTMLAudioElement | null | undefined,
): Promise<LegacyAudioPlaybackResult> {
  if (!audio) {
    return { ok: false, reason: 'missing-audio' }
  }

  try {
    await audio.play()
    return { ok: true }
  } catch (error) {
    return { ok: false, reason: getPlaybackFailureReason(error) }
  }
}

export function getLegacyAudioPlaybackStatusMessage(
  state: LegacyAudioPlaybackState,
  locale: string,
) {
  if (state !== 'starting' && state !== 'failed') {
    return null
  }

  return resolveLocalizedMapText(legacyAudioStatusMessages[state], locale)
}

export function resolveContentTitle(item: LearningContentItem, locale: string) {
  if (normalizeLearningLocale(locale) === 'es' && item.text?.trim()) {
    return item.text
  }

  return (
    resolveLocalizedDisplayText(item.title, locale) ??
    resolveTranslatedText(item.translates, locale) ??
    resolveLocalizedDisplayText(item.translate, locale) ??
    item.text
  )
}

export function resolveContentTranslation(item: LearningContentItem, locale: string) {
  return (
    resolveTranslatedText(item.translates, locale) ??
    resolveLocalizedDisplayText(item.translate, locale)
  )
}

export function resolveLegacyVocabularyTranslation(
  item: LearningContentItem,
  locale: string,
) {
  if (!Array.isArray(item.translates)) {
    return resolveTranslatedText(item.translates, locale)
  }

  for (const language of getLearningLocaleFallbacks(locale)) {
    const translation = item.translates.find((entry) => entry && typeof entry === 'object' && entry.code === language && typeof entry.text === 'string' && entry.text.trim())

    if (translation?.text) {
      return translation.text
    }
  }

  return undefined
}

export function getLegacyCardWidth(itemCount: number) {
  if (itemCount <= 1) {
    return '100%'
  }

  if (itemCount === 2) {
    return '50%'
  }

  return '33.33%'
}

export function useLegacyAudioPlayback(source?: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const attemptRef = useRef(0)
  const currentTimeRef = useRef(0)
  const [failureReason, setFailureReason] = useState<LegacyAudioPlaybackFailure | null>(null)
  const [playbackState, setPlaybackState] = useState<LegacyAudioPlaybackState>('idle')

  const handleAudioElement = useCallback((element: HTMLAudioElement | null) => {
    audioRef.current = element
  }, [])

  const toggleAudio = useCallback(async () => {
    const audio = audioRef.current

    if (!audio || !source) {
      setFailureReason('missing-audio')
      setPlaybackState('failed')
      return
    }

    // Like the original reader, native paused state decides play versus pause.
    // A pending play is already unpaused and must remain cancellable by a tap.
    if (!audio.paused) {
      attemptRef.current += 1
      audio.pause()
      setFailureReason(null)
      setPlaybackState('idle')
      return
    }

    const attempt = attemptRef.current + 1
    attemptRef.current = attempt
    setFailureReason(null)
    setPlaybackState('starting')

    try {
      audio.currentTime = currentTimeRef.current
    } catch {
      // A source without seekable metadata is still safe to start from zero.
    }

    const result = await playAudio(audio)

    if (attemptRef.current !== attempt || audioRef.current !== audio) {
      return
    }

    if (!result.ok) {
      setFailureReason(result.reason)
      setPlaybackState('failed')
      return
    }

    setPlaybackState(audio.paused ? 'idle' : 'playing')
  }, [source])

  return {
    audioProps: source
      ? {
          onEnded: () => {
            attemptRef.current += 1
            currentTimeRef.current = 0
            setFailureReason(null)
            setPlaybackState('idle')
          },
          onEmptied: () => {
            attemptRef.current += 1
            currentTimeRef.current = 0
            setFailureReason(null)
            setPlaybackState('idle')
          },
          onError: () => {
            attemptRef.current += 1
            setFailureReason('unavailable')
            setPlaybackState('failed')
          },
          onPause: () => {
            if (audioRef.current?.paused) {
              attemptRef.current += 1
              setPlaybackState('idle')
            }
          },
          onPlaying: () => {
            if (audioRef.current?.paused) {
              return
            }
            setFailureReason(null)
            setPlaybackState('playing')
          },
          onStalled: () => {
            if (!audioRef.current?.paused) {
              setPlaybackState('starting')
            }
          },
          onTimeUpdate: () => {
            currentTimeRef.current = audioRef.current?.currentTime ?? 0
          },
          onWaiting: () => {
            if (!audioRef.current?.paused) {
              setPlaybackState('starting')
            }
          },
          ref: handleAudioElement,
          src: source,
        }
      : null,
    isPlaying: playbackState === 'playing',
    failureReason,
    playbackState,
    toggleAudio,
  }
}

export function getNestedAudio(element: HTMLElement) {
  return element.querySelector('audio') as HTMLAudioElement | null
}
